// =============================================
// Pillar Generation Modal (STOP-P3 faza P)
//
// Modal progress dla generatePillar() / refreshPillarLinks() — pokazuje stages z onProgress callback.
// Po sukcesie pokazuje statystyki (cost, sections, linki w prozie, footer orphans, relations).
// =============================================

import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { generatePillar, refreshPillarLinks, type PillarGenerationResult } from "@/services/contentEngine/pillarGenerator";
import type { PillarLinkPostCheckResult } from "@/services/contentEngine/pillarLinkPostCheck";
import { uiLog } from "@/services/contentEngine/contentLogger";

export type PillarGenerationMode = "full" | "refresh_links";

export interface PillarGenerationModalProps {
  open: boolean;
  pillarNodeId: string;
  pillarTitle: string;
  mapId: string;
  mode: PillarGenerationMode;
  onClose: () => void;
  onComplete?: (success: boolean) => void;
}

interface StageLog {
  ts: number;
  stage: string;
  msg: string;
}

export function PillarGenerationModal({
  open, pillarNodeId, pillarTitle, mapId, mode, onClose, onComplete,
}: PillarGenerationModalProps) {
  const [running, setRunning] = useState(false);
  const [stages, setStages] = useState<StageLog[]>([]);
  const [result, setResult] = useState<PillarGenerationResult | null>(null);
  const [refreshResult, setRefreshResult] = useState<{ success: boolean; post_check: PillarLinkPostCheckResult | null; error?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Start generation gdy modal sie otworzy
  useEffect(() => {
    if (!open) {
      setRunning(false);
      setStages([]);
      setResult(null);
      setRefreshResult(null);
      setError(null);
      return;
    }

    let cancelled = false;
    const start = async () => {
      setRunning(true);
      setStages([{ ts: Date.now(), stage: "init", msg: `Start ${mode === "full" ? "generacji pillara" : "odswiezenia linkow"}: ${pillarTitle.slice(0, 60)}` }]);

      try {
        if (mode === "full") {
          const r = await generatePillar(pillarNodeId, mapId, (msg, stage) => {
            if (cancelled) return;
            setStages((prev) => [...prev, { ts: Date.now(), stage: stage || "stage", msg }]);
          });
          if (cancelled) return;
          setResult(r);
          if (!r.success) setError(r.error || "Generacja nieudana");
          uiLog.public("PillarGenModal", `Generacja ${r.success ? "OK" : "FAIL"} pillar=${pillarNodeId.slice(0, 8)} cost=$${r.costUsd.toFixed(4)}`);
          onComplete?.(r.success);
        } else {
          const r = await refreshPillarLinks(pillarNodeId, mapId);
          if (cancelled) return;
          setRefreshResult(r);
          if (!r.success) setError(r.error || "Refresh linkow nieudany");
          uiLog.public("PillarGenModal", `Refresh ${r.success ? "OK" : "FAIL"} pillar=${pillarNodeId.slice(0, 8)}`);
          onComplete?.(r.success);
        }
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        uiLog.error("PillarGenModal", `Exception: ${msg}`);
        onComplete?.(false);
      } finally {
        if (!cancelled) setRunning(false);
      }
    };

    start();
    return () => { cancelled = true; };
  }, [open, mode, pillarNodeId, mapId, pillarTitle, onComplete]);

  const debug = result?.debug;
  const postCheck = mode === "full" ? debug?.post_check : refreshResult?.post_check;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !running && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {mode === "full" ? "Generowanie pillar page" : "Odswiezanie linkow pillara"}
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium">{pillarTitle}</span>
            <br />
            <span className="text-xs text-muted-foreground">pillar_id: {pillarNodeId.slice(0, 8)} · map_id: {mapId.slice(0, 8)}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Stage progress log */}
          <div className="rounded-md border bg-muted/30 p-3 max-h-72 overflow-y-auto">
            <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2">
              Stages ({stages.length})
            </p>
            <ul className="space-y-1 text-xs font-mono">
              {stages.map((s, i) => (
                <li key={`${s.ts}-${i}`} className="flex items-start gap-2">
                  {running && i === stages.length - 1 ? (
                    <Loader2 className="h-3 w-3 animate-spin shrink-0 mt-0.5" />
                  ) : (
                    <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0 mt-0.5" />
                  )}
                  <span className="text-muted-foreground">[{s.stage}]</span>
                  <span className="break-all">{s.msg}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-destructive">Blad</p>
                <p className="text-xs font-mono break-all">{error}</p>
              </div>
            </div>
          )}

          {/* Success stats (full mode) */}
          {!running && result?.success && debug && (
            <div className="rounded-md border border-green-500/30 bg-green-500/5 p-3 space-y-2">
              <p className="text-sm font-semibold text-green-700 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> Pillar wygenerowany
              </p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Stat label="Sekcje H2" value={debug.sections_count} />
                <Stat label="Final HTML" value={`${debug.final_html_chars} chars`} />
                <Stat label="Spoki seeda" value={debug.cluster_member_spokes_count} />
                <Stat label="Published spoków" value={debug.published_spokes_count} />
                <Stat label="Relations (PRE)" value={`+${debug.pre_step_relations_created} / -${debug.pre_step_relations_deleted}`} />
                <Stat label="Cost" value={`$${debug.total_cost_usd.toFixed(4)}`} />
                {postCheck && (
                  <>
                    <Stat label="Linki w prozie" value={postCheck.links_in_prose.length} />
                    <Stat label="Dedupe" value={postCheck.deduplicated_count} />
                    <Stat label="Sieroty (forensics)" value={postCheck.orphans.length} />
                    <Stat label="Relations refreshed" value={postCheck.node_relations_refreshed} />
                  </>
                )}
                <Stat label="Duration" value={`${(debug.total_duration_ms / 1000).toFixed(1)}s`} />
              </div>
            </div>
          )}

          {/* Success stats (refresh mode) */}
          {!running && refreshResult?.success && postCheck && (
            <div className="rounded-md border border-green-500/30 bg-green-500/5 p-3 space-y-2">
              <p className="text-sm font-semibold text-green-700 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> Linki odswiezone
              </p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Stat label="Linki w prozie" value={postCheck.links_in_prose.length} />
                <Stat label="Dedupe" value={postCheck.deduplicated_count} />
                <Stat label="Sieroty (forensics)" value={postCheck.orphans.length} />
                <Stat label="Relations refreshed" value={postCheck.node_relations_refreshed} />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose} disabled={running}>
            {running ? <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> W toku...</> : "Zamknij"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-baseline justify-between gap-2 rounded bg-card px-2 py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono font-semibold">{value}</span>
    </div>
  );
}
